import { useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { Link, matchPath, useLocation } from 'react-router-dom'
import { useUser } from '@mfox/shared/contexts/UserContext'
import { featureHidden, type AlphaFeature } from '@mfox/shared/lib/alpha'
import { useMediaQuery } from '@mfox/shared/lib/useMediaQuery'
import { AccountAvatar, AccountItems, AccountMenu, accountLines } from '@mfox/shared/ui/AccountMenu'
import { Dialog } from '@mfox/shared/ui/Dialog'
import { Logo } from '@mfox/shared/ui/Logo'
import { RowLink } from '@mfox/shared/ui/Row'
import { ToastHost } from '@mfox/shared/ui/ToastHost'
import './AppFrame.css'

export interface AppFrameTab {
  to: string
  label: string
  /** Match the path exactly, for a section's home tab ("/mentor"). */
  end?: boolean
  /** Other sections this tab owns, e.g. the leaderboard under Home. */
  also?: string[]
  /** An alpha feature this tab leads to; the tab is left out of a build that hides it. */
  feature?: AlphaFeature
}

export type AppFrameIcon = 'home' | 'course' | 'tests' | 'roadmap'

/** A tab in the phone's bottom bar: a short label and an icon. */
export interface AppFrameBarTab extends Omit<AppFrameTab, 'feature'> {
  icon: AppFrameIcon
}

function isTabActive({ to, end = false, also = [] }: Omit<AppFrameTab, 'label'>, pathname: string): boolean {
  return [to, ...also].some((path, i) => matchPath({ path, end: i === 0 ? end : false }, pathname) !== null)
}

const ICON_PATHS: Record<AppFrameIcon | 'more', ReactNode> = {
  home: <path d="M3.5 10L11 4l7.5 6v8.5h-5V13h-5v5.5h-5z" />,
  course: <path d="M11 6.5C9 5 6.5 4.5 3.5 5v12c3-.5 5.5 0 7.5 1.5 2-1.5 4.5-2 7.5-1.5V5c-3-.5-5.5 0-7.5 1.5zM11 6.5V18" />,
  tests: (
    <>
      <rect x="4.5" y="3.5" width="13" height="15.5" rx="2" />
      <path d="M8 11l2 2 4-4.5" />
    </>
  ),
  roadmap: (
    <>
      <circle cx="6" cy="17" r="2" />
      <circle cx="16" cy="5" r="2" />
      <path d="M8 17h5.5a3 3 0 0 0 0-6h-5a3 3 0 0 1 0-6H14" />
    </>
  ),
  more: (
    <g fill="currentColor" stroke="none">
      <circle cx="5" cy="11" r="1.6" />
      <circle cx="11" cy="11" r="1.6" />
      <circle cx="17" cy="11" r="1.6" />
    </g>
  ),
}

function TabIcon({ name }: { name: AppFrameIcon | 'more' }) {
  return (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" aria-hidden="true">
      {ICON_PATHS[name]}
    </svg>
  )
}

export interface AppFrameProps {
  tabs: AppFrameTab[]
  /** The nav landmark's name, e.g. "Sections". */
  navLabel: string
  /** Phones show these as a bottom tab bar, with a More sheet for every other tab and the account, instead of the
   *  slide-in menu. Laptops keep the sidebar either way. */
  bar?: AppFrameBarTab[]
  /** A small label after the wordmark for staff areas: "admin", "college", "mentor". */
  roleLabel?: string
  /** The role's profile page, linked from the account menu. */
  profileTo?: string
  /** Usually a StatusTicker, kept at the bottom of the viewport while the page scrolls. */
  ticker?: ReactNode
  /** 'viewport' locks the frame to the screen height so the content scrolls its own regions (the course player,
   *  the course editor). Below 901px it falls back to a normal scrolling page. */
  variant?: 'page' | 'viewport'
  /** False hides the frame's own navigation, top bar and ticker. */
  chrome?: boolean
  /** A page's own top bar (the lesson player's), at the top of the page column with the XP notices under it. */
  top?: ReactNode
  children: ReactNode
}

/**
 * The one layout for every signed-in area: on a laptop a sidebar (the logo, the tabs, the account at its foot),
 * the page, and an optional status ticker. On a phone a top bar with the logo and the account, and either a bottom
 * tab bar (`bar`) or a slide-in menu. It knows nothing about roles; each area passes its own tabs and profile page.
 */
export function AppFrame({ tabs, navLabel, bar, roleLabel, profileTo, ticker, variant = 'page', chrome = true, top, children }: AppFrameProps) {
  const { pathname } = useLocation()
  const phone = useMediaQuery('(max-width: 900px)')
  const tabBar = chrome && !!bar && phone
  const tabsRef = useRef<HTMLUListElement>(null)
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const navRef = useRef<HTMLElement>(null)
  const wasOpen = useRef(false)
  const navId = useId()
  // Below 901px, without a tab bar, the sidebar is a drawer that slides in from the left.
  const [menuOpen, setMenuOpen] = useState(false)
  const [moreOpen, setMoreOpen] = useState(false)
  const shownTabs = tabs.filter((tab) => !tab.feature || !featureHidden(tab.feature))

  // The tab list scrolls; keep the current tab in view.
  useEffect(() => {
    tabsRef.current?.querySelector<HTMLElement>('.app-frame__tab--active')?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' })
  }, [pathname])

  // A tab was chosen, or the window grew past the drawer breakpoint: the drawer and the sheet have nothing left to do.
  useEffect(() => {
    setMenuOpen(false)
    setMoreOpen(false)
  }, [pathname])
  useEffect(() => {
    if (!phone) {
      setMenuOpen(false)
      setMoreOpen(false)
    }
  }, [phone])

  // While open: focus the current tab, Escape closes and hands focus back to the menu button, the page behind
  // does not scroll (the rest of the frame is inert, below).
  useEffect(() => {
    if (!menuOpen) return
    const tabsEl = tabsRef.current
    ;(tabsEl?.querySelector<HTMLElement>('.app-frame__tab--active') ?? tabsEl?.querySelector<HTMLElement>('a'))?.focus()
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeMenu()
    }
    const overflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.body.style.overflow = overflow
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [menuOpen])

  // After closing, hand focus back to the menu button if it was in the drawer. This runs once the head is no
  // longer inert; focusing it in the same handler that closes the drawer is ignored by the browser.
  useEffect(() => {
    if (wasOpen.current && !menuOpen) {
      const active = document.activeElement
      if (!active || active === document.body || navRef.current?.contains(active)) menuButtonRef.current?.focus()
    }
    wasOpen.current = menuOpen
  }, [menuOpen])

  function closeMenu() {
    setMenuOpen(false)
  }

  const classes = [
    'app-frame',
    variant === 'viewport' && 'app-frame--viewport',
    !chrome && 'app-frame--bare',
    tabBar && 'app-frame--tabbar',
  ].filter(Boolean).join(' ')

  return (
    <div className={classes}>
      {chrome && (
        <header className="app-frame__bar">
          <div className="app-frame__head" inert={menuOpen}>
            {tabBar ? null : (
              <button
                ref={menuButtonRef}
                type="button"
                className="app-frame__menu"
                aria-label="Open menu"
                aria-expanded={menuOpen}
                aria-controls={navId}
                onClick={() => setMenuOpen(true)}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                  <path d="M4 7h16M4 12h16M4 17h16" />
                </svg>
              </button>
            )}
            <Logo to="/" className="app-frame__brand" />
            {roleLabel ? <span className="app-frame__role">{roleLabel}</span> : null}
          </div>
          {menuOpen ? <div className="app-frame__scrim" onClick={closeMenu} aria-hidden="true" /> : null}
          {tabBar ? null : (
            <nav ref={navRef} id={navId} className={`app-frame__nav${menuOpen ? ' app-frame__nav--open' : ''}`} aria-label={navLabel}>
              <div className="app-frame__navhead">
                <span className="app-frame__navtitle">Menu</span>
                <button type="button" className="app-frame__close" aria-label="Close menu" onClick={closeMenu}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                    <path d="M6 6l12 12M18 6L6 18" />
                  </svg>
                </button>
              </div>
              <ul className="app-frame__tabs" ref={tabsRef}>
                {shownTabs.map((tab) => {
                  const active = isTabActive(tab, pathname)
                  return (
                    <li key={tab.to}>
                      <Link
                        to={tab.to}
                        className={`app-frame__tab${active ? ' app-frame__tab--active' : ''}`}
                        aria-current={active ? 'page' : undefined}
                        onClick={() => setMenuOpen(false)}
                      >
                        {tab.label}
                      </Link>
                    </li>
                  )
                })}
              </ul>
            </nav>
          )}
          <div className="app-frame__account" inert={menuOpen}>
            <AccountMenu profileTo={profileTo} showName={!phone} />
          </div>
        </header>
      )}
      {/* One column for the page and the ticker, so the ticker's sticky bottom spans the whole page's scroll. XP
          notices sit at its top, under the top bar, and push the page down. */}
      <div className="app-frame__page" inert={menuOpen}>
        {top}
        <ToastHost />
        <main className="app-frame__main">
          <div className="app-frame__content">{children}</div>
        </main>
        {chrome && ticker ? <div className="app-frame__ticker">{ticker}</div> : null}
      </div>
      {tabBar && bar ? (
        <TabBar
          bar={bar}
          more={shownTabs.filter((tab) => !bar.some((item) => item.to === tab.to))}
          navLabel={navLabel}
          pathname={pathname}
          profileTo={profileTo}
          moreOpen={moreOpen}
          setMoreOpen={setMoreOpen}
        />
      ) : null}
    </div>
  )
}

interface TabBarProps {
  bar: AppFrameBarTab[]
  /** The tabs that don't fit in the bar, listed in the More sheet. */
  more: AppFrameTab[]
  navLabel: string
  pathname: string
  profileTo?: string
  moreOpen: boolean
  setMoreOpen: (open: boolean) => void
}

/** The phone's bottom tab bar and its More sheet. */
function TabBar({ bar, more, navLabel, pathname, profileTo, moreOpen, setMoreOpen }: TabBarProps) {
  const { currentUser } = useUser()
  const active = bar.find((tab) => isTabActive(tab, pathname))
  const lines = currentUser ? accountLines(currentUser) : null

  return (
    <>
      <nav className="app-frame__tabbar" aria-label={navLabel}>
        {bar.map((tab) => (
          <Link
            key={tab.to}
            to={tab.to}
            className={`app-frame__tabbar-item${tab === active ? ' app-frame__tabbar-item--active' : ''}`}
            aria-current={tab === active ? 'page' : undefined}
          >
            <TabIcon name={tab.icon} />
            {tab.label}
          </Link>
        ))}
        {/* Every page that isn't in the bar is under More, so it shows as the current place there. */}
        <button
          type="button"
          className={`app-frame__tabbar-item${active ? '' : ' app-frame__tabbar-item--active'}`}
          aria-haspopup="dialog"
          aria-expanded={moreOpen}
          onClick={() => setMoreOpen(true)}
        >
          <TabIcon name="more" />
          More
        </button>
      </nav>
      {moreOpen ? (
        <Dialog title="More" placement="sheet" className="app-frame__more" onClose={() => setMoreOpen(false)}>
          {more.length > 0 ? (
            <ul className="app-frame__more-tabs">
              {more.map((tab) => (
                <li key={tab.to}>
                  <RowLink to={tab.to} aria-current={isTabActive(tab, pathname) ? 'page' : undefined}>
                    {tab.label}
                  </RowLink>
                </li>
              ))}
            </ul>
          ) : null}
          {currentUser && lines ? (
            <div className="app-frame__more-account">
              <p className="app-frame__more-who">
                <AccountAvatar user={currentUser} />
                <span className="app-frame__more-lines">
                  <span className="app-frame__more-name">{lines.name}</span>
                  <span className="app-frame__more-detail">{lines.detail}</span>
                </span>
              </p>
              <AccountItems profileTo={profileTo} onChoose={() => setMoreOpen(false)} />
            </div>
          ) : null}
        </Dialog>
      ) : null}
    </>
  )
}
